import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import { artifactSiteLine } from 'features/knowledgeDocumentViewer/artifacts/artifactDate'
import {
  countMissingMarkers,
  missingReasonsFor,
  sectionMissingReasons,
  tokenizeMissingMarkers,
} from 'features/knowledgeDocumentViewer/artifacts/missingMarkers'
import type {
  DocumentArtifact,
  DocumentArtifactSection,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import React, { useMemo } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import {
  MISSING_MARKER_PROPERTY,
  rehypeMissingMarkers,
} from './rehypeMissingMarkers'
import {
  Blockquote,
  BodyCell,
  type CellAlign,
  ChangedSection,
  CodeBlock,
  Emphasis,
  HeadCell,
  List,
  MissingValue,
  Mono,
  OrderedList,
  Paragraph,
  SectionHeading,
  SheetLink,
  SheetMutedLine,
  SheetTitle,
  Strike,
  Strong,
  SubHeading,
  Table,
  UpdatedCaption,
} from './SheetPrimitives'

const NUMERIC_CELL = /^[\s$€£()+\-,.%\d]*\d[\s$€£()+\-,.%\d]*$/

function cellAlign(
  align: string | undefined,
  style: React.CSSProperties | undefined,
  content: React.ReactNode,
  isBody: boolean
): CellAlign | undefined {
  const declared = align ?? style?.textAlign
  if (declared === 'right' || declared === 'center' || declared === 'left') {
    return declared
  }
  if (!isBody) return undefined
  const text = React.Children.toArray(content)
    .filter((child): child is string => typeof child === 'string')
    .join('')
    .trim()
  return text.length > 0 && NUMERIC_CELL.test(text) ? 'right' : undefined
}

function sheetComponents(
  reasons: readonly string[],
  markerCount: number
): Components {
  return {
    h1: ({ children }) => <SubHeading>{children}</SubHeading>,
    h2: ({ children }) => <SubHeading>{children}</SubHeading>,
    h3: ({ children }) => <SubHeading>{children}</SubHeading>,
    h4: ({ children }) => <SubHeading>{children}</SubHeading>,
    h5: ({ children }) => <SubHeading>{children}</SubHeading>,
    h6: ({ children }) => <SubHeading>{children}</SubHeading>,
    p: ({ children }) => <Paragraph>{children}</Paragraph>,
    blockquote: ({ children }) => <Blockquote>{children}</Blockquote>,
    ul: ({ children }) => <List>{children}</List>,
    ol: ({ children, start }) => (
      <OrderedList start={start}>{children}</OrderedList>
    ),
    strong: ({ children }) => <Strong>{children}</Strong>,
    em: ({ children }) => <Emphasis>{children}</Emphasis>,
    del: ({ children }) => <Strike>{children}</Strike>,
    a: ({ children, href }) => (
      <SheetLink href={href} target='_blank' rel='noopener noreferrer'>
        {children}
      </SheetLink>
    ),
    img: () => null,
    pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
    code: ({ children }) => <Mono as='code'>{children}</Mono>,
    span: ({ children, node }) => {
      const marker = node?.properties[MISSING_MARKER_PROPERTY]
      if (marker === undefined) return <span>{children}</span>
      return (
        <MissingValue
          reasons={missingReasonsFor(reasons, Number(marker), markerCount)}
        />
      )
    },
    table: ({ children }) => <Table>{children}</Table>,
    th: ({ children, align, style }) => (
      <HeadCell $align={cellAlign(align, style, children, false)}>
        {children}
      </HeadCell>
    ),
    td: ({ children, align, style }) => (
      <BodyCell $align={cellAlign(align, style, children, true)}>
        {children}
      </BodyCell>
    ),
  }
}

function SectionBody({
  artifact,
  section,
}: {
  artifact: DocumentArtifact
  section: DocumentArtifactSection
}): React.ReactElement {
  const reasons = useMemo(
    () => sectionMissingReasons(section, artifact.content.open_items),
    [artifact.content.open_items, section]
  )
  const markerCount = countMissingMarkers(section.body)
  const components = useMemo(
    () => sheetComponents(reasons, markerCount),
    [reasons, markerCount]
  )
  const body = useMemo(
    () => tokenizeMissingMarkers(section.body),
    [section.body]
  )
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeMissingMarkers]}
      components={components}
      skipHtml>
      {body}
    </ReactMarkdown>
  )
}

/**
 * The freeform document kind as a paper sheet: title, site and date, then
 * markdown sections. Sections named in `changedPaths` carry the updated outline.
 */
export function FreeformDocumentSheet({
  artifact,
  changedPaths = [],
}: {
  artifact: DocumentArtifact
  changedPaths?: string[]
}): React.ReactElement {
  useLingui()
  return (
    <div data-testid='freeform-document-sheet'>
      <SheetTitle>
        {artifact.title}
        {changedPaths.includes('title') ? (
          <UpdatedCaption>
            <Trans>Updated</Trans>
          </UpdatedCaption>
        ) : null}
      </SheetTitle>
      <SheetMutedLine>{artifactSiteLine(artifact)}</SheetMutedLine>
      {artifact.content.sections.map((section) => {
        const changed = changedPaths.includes(section.section_id)
        return (
          <ChangedSection
            key={section.section_id}
            data-section-id={section.section_id}
            data-changed={changed ? '' : undefined}
            $changed={changed}>
            <SectionHeading>
              {section.heading}
              {changed ? (
                <UpdatedCaption>
                  <Trans>Updated</Trans>
                </UpdatedCaption>
              ) : null}
            </SectionHeading>
            <SectionBody artifact={artifact} section={section} />
          </ChangedSection>
        )
      })}
    </div>
  )
}
